import './AppHeaderComponent.css';
import React, {FC, useEffect, useState} from 'react';
import logo from '../../assets/bp_logo.png';
import {useLocation, useNavigate} from 'react-router-dom';
import {useLocalizedNavigate} from '../../hooks/useLocalizedNavigate';
import {
    appLanguageToLangCode,
    buildLocalizedPath,
    stripLangFromPath,
    SupportedLangCode,
} from '../../config/languages';
import {
    AppBar,
    Avatar,
    Button,
    Divider,
    FormControl,
    IconButton,
    Menu,
    MenuItem,
    Select,
    SvgIcon,
    Toolbar,
    Tooltip,
    useMediaQuery,
} from '@mui/material';
import {colors} from '../../colors/Colors';
import {useTheme} from '@mui/material/styles';
import {ReactComponent as MenuIcon} from '../../assets/menu.svg';
import {currentUser, isAdmin, isLoggedIn, logout} from '../../store/UserStore';
import Typography from '@mui/material/Typography';
import {AppLanguage, ModalAction, UserRole} from '../../model/Types';
import {ReactComponent as ProfileIcon} from '../../assets/profile.svg';
import Input from '@mui/material/Input';
import {appLanguage, setLanguage} from '../../store/LanguageStore';
import {useTranslation} from 'react-i18next';
import {ConfirmationModalComponent} from '../confirmation-modal/ConfirmationModalComponent';

export const AppHeaderComponent: FC = () => {
    const [scrolled, setScrolled] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useLocalizedNavigate();
    const rawNavigate = useNavigate();
    const location = useLocation();
    const isHomePage = location.pathname === '/' || /^\/(sr|en|de|fr|ru)\/?$/.test(location.pathname);
    const [profilePicture, setProfilePicture] = useState<Uint8Array>(new Uint8Array());
    const [currentLanguage, setCurrentLanguage] = useState<AppLanguage>(
        appLanguage.value || AppLanguage.EN,
    );
    const { t } = useTranslation();

    const ITEM_HEIGHT = 48;
    const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
    const openMenu = Boolean(menuAnchorEl);
    const menuClicked = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };
    const [logoutModalOpened, setLogoutModalOpened] = useState<boolean>(false);

    enum MENU_ITEMS_ANONYMOUS {
        THERAPISTS = 'Therapists',
        WORKSHOPS = 'Workshops_public',
        BLOG = 'Blog',
        LOGIN = 'Login',
    }

    enum MENU_ITEMS_LOGGED_IN {
        THERAPISTS = 'Therapists',
        WORKSHOPS = 'Workshops_public',
        BLOG = 'Blog',
        PROFILE = 'Profile',
        LOGOUT = 'Logout',
    }

    enum MENU_ITEMS_ADMIN {
        WORKSHOP_MANAGEMENT = 'Workshops',
        PATIENT_MANAGEMENT = 'P. Management',
        CALENDAR_AVAILABILITY_MANAGEMENT = 'CA Management',
        SESSION_MANAGEMENT = 'S. Management',
        HISTORY = 'History',
        ADD_THERAPIST = 'Add Therapist',
        THERAPISTS = 'Therapists',
        LOGOUT = 'Logout',
    }

    const ADMIN_BUTTON_SX = {
        ':hover': {
            display: 'inline-block',
            borderBottom: '1px solid',
            borderColor: colors.background,
            radius: '0',
            borderRadius: '0',
            backgroundColor: 'transparent',
            marginBottom: '4px',
        },
        color: colors.background,
        fontWeight: '600',
        fontSize: '0.8rem',
    };

    const BUTTON_SX = {
        ':hover': {
            display: 'inline-block',
            borderBottom: '1px solid',
            borderColor: colors.background,
            radius: '0',
            borderRadius: '0',
            backgroundColor: 'transparent',
            marginBottom: '4px',
        },
        color: colors.background,
        fontWeight: '600',
    }

    useEffect(() => {
        setCurrentLanguage(appLanguage.value || AppLanguage.EN);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 30); // 50px threshold
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (currentUser.value?.avatar && profilePicture.length === 0) {
            setProfilePicture(currentUser.value.avatar);
        }
    }, [profilePicture]);

    const onLogoClicked = () => {
        navigate('/');
    };

    const onMenuItemClicked = (event: any) => {
        switch (event.target.id) {
            case MENU_ITEMS_ADMIN.ADD_THERAPIST:
                navigateToAddTherapist();
                break;
            case MENU_ITEMS_ADMIN.WORKSHOP_MANAGEMENT:
                navigateToWorkshopManagement();
                break;
            case MENU_ITEMS_ADMIN.PATIENT_MANAGEMENT:
                navigateToPatientManagement();
                break;
            case MENU_ITEMS_ADMIN.CALENDAR_AVAILABILITY_MANAGEMENT:
                navigateToCaManagement();
                break;
            case MENU_ITEMS_ADMIN.SESSION_MANAGEMENT:
                navigateToSessionManagement();
                break;
            case MENU_ITEMS_ADMIN.HISTORY:
                navigateToHistory();
                break;
            case MENU_ITEMS_LOGGED_IN.PROFILE:
                navigateToProfile();
                break;
            case MENU_ITEMS_LOGGED_IN.LOGOUT:
            case MENU_ITEMS_ADMIN.LOGOUT:
                openLogoutModal();
                break;
            case MENU_ITEMS_LOGGED_IN.THERAPISTS:
            case MENU_ITEMS_ANONYMOUS.THERAPISTS:
            case MENU_ITEMS_ADMIN.THERAPISTS:
                navigateToTherapists();
                break;
            case MENU_ITEMS_ANONYMOUS.WORKSHOPS:
            case MENU_ITEMS_LOGGED_IN.WORKSHOPS:
                navigateToWorkshops();
                break;
            case MENU_ITEMS_ANONYMOUS.BLOG:
            case MENU_ITEMS_LOGGED_IN.BLOG:
                navigateToBlog();
                break;
            case MENU_ITEMS_ANONYMOUS.LOGIN:
                navigateToLogin();
                break;
            default:
                console.warn('Unknown menu option clicked:', event.target.innerText);
                break;
        }
        setMenuAnchorEl(null);
    };

    const navigateToAddTherapist = () => {
        navigate('/adm/addTherapist');
    };

    const navigateToTherapists = () => {
        navigate('/trps');
    };

    const navigateToWorkshops = () => {
        navigate('/ws');
    };

    const navigateToBlog = () => {
        navigate('/blog');
    };

    const navigateToLogin = () => {
        navigate('/user/login');
    };

    const navigateToProfile = () => {
        navigate('/user/profile');
    };

    const navigateToHistory = () => {
        navigate('/adm/history');
    };

    const navigateToSessionManagement = () => {
        navigate('/adm/session/management');
    };

    const navigateToCaManagement = () => {
        navigate('/adm/calendar-availability/management');
    };

    const navigateToWorkshopManagement = () => {
        navigate('/adm/ws/management');
    };


    const navigateToPatientManagement = () => {
        navigate('/adm/patient/management');
    };

    const openLogoutModal = () => {
        setLogoutModalOpened(true);
    };

    const logoutUser = () => {
        logout();
        navigate('/');
    };

    const getBackgroundColor = () => {
        if (isHomePage) {
            return scrolled ? 'rgba(138, 182, 160, 0.7)' : 'transparent';
        }
        return 'rgba(138, 182, 160, 0.7)';
    };

    const getProfileIcon = () => {
        if (currentUser.value?.role === UserRole.USER) {
            return (
                <IconButton aria-label="profile">
                    <SvgIcon component={ProfileIcon} />
                </IconButton>
            );
        } else if (currentUser.value?.role === UserRole.THERAPIST) {
            return (
                profilePicture && (
                    <Avatar
                        src={`data:image/png;base64,${profilePicture}`}
                        sx={{ width: '1.6rem', height: '1.6rem' }}
                    />
                )
            );
        }
        return <div></div>;
    };

    const onLanguageChange = (event: any) => {
        const newLang: AppLanguage = event.target.value;
        setCurrentLanguage(newLang);
        setLanguage(newLang);

        // Update the URL so the URL always carries the current UI language.
        // Uses raw react-router navigate (not useLocalizedNavigate) because we
        // need to strip the existing lang prefix and apply the NEW one.
        const code: SupportedLangCode = appLanguageToLangCode(newLang);
        const currentPath = stripLangFromPath(location.pathname);
        const newPath =
            buildLocalizedPath(currentPath, code) + location.search + location.hash;
        rawNavigate(newPath, { replace: true });
    };

    const getStyledInput = () => {
        return (
            <Input
                sx={{
                    color: colors.background,
                    '& .MuiSelect-icon': {
                        color: `${colors.background} !important`,
                    },
                    '&:before': {
                        borderBottom: `${colors.background} !important`,
                    },
                    '&:hover:not(.Mui-disabled, .Mui-error):before': {
                        borderBottom: `${colors.background} !important`,
                    },
                    '&:after': {
                        borderBottom: `${colors.background} !important`,
                    },
                }}
            />
        );
    };

    const onModalClose = () => {
        setLogoutModalOpened(false);
    };

    const onModalConfirmed = () => {
        setLogoutModalOpened(false);
        logoutUser();
    };

    return (
        <>
            <ConfirmationModalComponent
                open={logoutModalOpened}
                onClose={onModalClose}
                onConfirm={onModalConfirmed}
                text={t('header.logoutModal.text')}
                action={ModalAction.LOGOUT}
                isMobile={isMobile}
            />
            <AppBar
                position="sticky"
                sx={{
                    backgroundColor: getBackgroundColor(),
                    backdropFilter: scrolled ? 'blur(3px)' : 'none',
                    transition: 'background-color 0.3s ease-in-out',
                    position: 'fixed',
                    boxShadow: 'none',
                    paddingRight: isMobile ? '1rem' : '2rem',
                    paddingLeft: isMobile ? '1rem' : '1.5rem',
                }}
            >
                <Toolbar>
                    <div className="img-container">
                        <img
                            src={logo}
                            alt="logo"
                            aria-label="logo icon"
                            className="header-img"
                            onClick={onLogoClicked}
                        />
                    </div>
                    {!isMobile && (
                        <div className="header-buttons">
                            {isAdmin.value && (
                                <div className="logged-in-content">
                                    <Button
                                        variant="text"
                                        aria-label="workshop management button"
                                        sx={ADMIN_BUTTON_SX}
                                        onClick={navigateToWorkshopManagement}
                                    >
                                        {t('header.buttons.workshopManagement')}
                                    </Button>
                                    <Button
                                        variant="text"
                                        aria-label="patient management button"
                                        sx={ADMIN_BUTTON_SX}
                                        onClick={navigateToPatientManagement}
                                    >
                                        {t('header.buttons.patientManagement')}
                                    </Button>
                                    <Button
                                        variant="text"
                                        aria-label="calendar availability management button"
                                        sx={ADMIN_BUTTON_SX}
                                        onClick={navigateToCaManagement}
                                    >
                                        {t('header.buttons.calendarAvailabilityManagement')}
                                    </Button>
                                    <Button
                                        variant="text"
                                        aria-label="session management button"
                                        sx={ADMIN_BUTTON_SX}
                                        onClick={navigateToSessionManagement}
                                    >
                                        {t('header.buttons.sessionManagement')}
                                    </Button>
                                    <Button
                                        variant="text"
                                        aria-label="history button"
                                        sx={ADMIN_BUTTON_SX}
                                        onClick={navigateToHistory}
                                    >
                                        {t('header.buttons.history')}
                                    </Button>
                                    <Button
                                        variant="text"
                                        aria-label="add therapist button"
                                        sx={ADMIN_BUTTON_SX}
                                        onClick={navigateToAddTherapist}
                                    >
                                        {t('header.buttons.addTherapist')}
                                    </Button>
                                </div>
                            )}
                            <Button
                                variant="text"
                                aria-label="therapists button"
                                sx={BUTTON_SX}
                                onClick={navigateToTherapists}
                            >
                                {t('header.buttons.therapists')}
                            </Button>
                            {!isAdmin.value && (
                                <Button
                                    variant="text"
                                    aria-label="workshops button"
                                    sx={BUTTON_SX}
                                    onClick={navigateToWorkshops}
                                >
                                    {t('header.buttons.workshops')}
                                </Button>
                            )}
                            {!isAdmin.value && (
                                <Button
                                    variant="text"
                                    aria-label="blog button"
                                    sx={BUTTON_SX}
                                    onClick={navigateToBlog}
                                >
                                    {t('common.menuItems.anonymous.blog')}
                                </Button>
                            )}
                            {isLoggedIn.value && (
                                <div className="logged-in-content">
                                    <Divider
                                        orientation="vertical"
                                        variant="middle"
                                        flexItem
                                        sx={{ backgroundColor: colors.background }}
                                    />
                                    <div className="greeting" style={{ color: colors.background }}>
                                        <Typography>
                                            {t('header.greeting')}{' '}
                                            {isAdmin.value ? 'Admin' : currentUser.value?.firstName}
                                        </Typography>
                                    </div>
                                    <Tooltip
                                        title={t('header.profile')}
                                        placement="bottom"
                                        onClick={navigateToProfile}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {getProfileIcon()}
                                    </Tooltip>
                                    <Button
                                        variant="text"
                                        aria-label="logout button"
                                        sx={BUTTON_SX}
                                        onClick={openLogoutModal}
                                    >
                                        {t('header.logout')}
                                    </Button>
                                </div>
                            )}
                            {!isLoggedIn.value && (
                                <Button
                                    variant="text"
                                    aria-label="login button"
                                    sx={BUTTON_SX}
                                    onClick={navigateToLogin}
                                >
                                    {t('header.login')}
                                </Button>
                            )}
                            <FormControl sx={{ m: 1, minWidth: 60 }}>
                                <Select
                                    id="appLanguage"
                                    name="appLanguage"
                                    label="Language"
                                    fullWidth
                                    defaultValue=""
                                    value={currentLanguage}
                                    onChange={onLanguageChange}
                                    autoWidth
                                    variant="standard"
                                    input={getStyledInput()}
                                >
                                    {Object.values(AppLanguage).map((language: AppLanguage) => (
                                        <MenuItem key={language} value={language}>
                                            {language}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    )}
                    {isMobile && (
                        <div className="mobile-menus">
                            <FormControl sx={{ m: 1, minWidth: 60 }}>
                                <Select
                                    id="appLanguage-mobile"
                                    name="appLanguage-mobile"
                                    label="Language"
                                    fullWidth
                                    defaultValue=""
                                    value={currentLanguage}
                                    onChange={onLanguageChange}
                                    autoWidth
                                    variant="standard"
                                    input={getStyledInput()}
                                >
                                    {Object.values(AppLanguage).map((language: AppLanguage) => (
                                        <MenuItem key={language} value={language}>
                                            {language}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <IconButton aria-label="menu" onClick={menuClicked}>
                                <SvgIcon
                                    style={{ width: '40px', height: 'auto' }}
                                    component={MenuIcon}
                                />
                            </IconButton>
                            <Menu
                                id="menu"
                                anchorEl={menuAnchorEl}
                                open={openMenu}
                                onClose={onMenuItemClicked}
                                slotProps={{
                                    paper: {
                                        style: {
                                            maxHeight: ITEM_HEIGHT * 4.5,
                                            width: '20ch',
                                        },
                                    },
                                    list: {
                                        'aria-labelledby': 'long-button',
                                    },
                                }}
                            >
                                {!isLoggedIn.value &&
                                    Object.values(MENU_ITEMS_ANONYMOUS).map((option) => (
                                        <MenuItem
                                            key={option}
                                            id={option}
                                            onClick={onMenuItemClicked}
                                            style={{ color: colors.grey }}
                                        >
                                            {t(
                                                `common.menuItems.anonymous.${option.toLowerCase()}`,
                                            )}
                                        </MenuItem>
                                    ))}
                                {isLoggedIn.value &&
                                    !isAdmin.value &&
                                    Object.values(MENU_ITEMS_LOGGED_IN).map((option) => (
                                        <MenuItem
                                            key={option}
                                            id={option}
                                            onClick={onMenuItemClicked}
                                            style={{ color: colors.grey }}
                                        >
                                            {t(`common.menuItems.loggedIn.${option.toLowerCase()}`)}
                                        </MenuItem>
                                    ))}
                                {isLoggedIn.value &&
                                    isAdmin.value &&
                                    Object.values(MENU_ITEMS_ADMIN).map((option) => (
                                        <MenuItem
                                            key={option}
                                            id={option}
                                            onClick={onMenuItemClicked}
                                            style={{ color: colors.grey }}
                                        >
                                            {option}
                                        </MenuItem>
                                    ))}
                            </Menu>
                        </div>
                    )}
                </Toolbar>
            </AppBar>
        </>
    );
};
